import React, { useState, useEffect, useRef } from 'react';
import {
  Modal, Form, Button, Row, Col, Alert, Spinner, InputGroup
} from 'react-bootstrap';
import {
  Save, X, Shirt, Clock, DollarSign, Tag, FileText,
  Upload, Image as ImageIcon, Star, Trash2, Plus
} from 'lucide-react';
import modelService from '../../../services/modelService';

/**
 * ✅ ATELIER: Modal de création / édition d'un modèle.
 *
 * ✅ MODIFIÉ:
 *   - Suppression de l'image du tissu
 *   - Galerie multi-images :
 *       * À la création : on sélectionne plusieurs fichiers, uploadés après création.
 *         La 1ère devient automatiquement principale.
 *       * À l'édition : la galerie existante est affichée. Chaque image a :
 *           ⭐ pour la définir comme principale
 *           🗑️ pour la supprimer
 *         Un bouton "+ Ajouter" uploade immédiatement de nouvelles images.
 */
const CreateModelModal = ({
  show,
  onHide,
  onSuccess,
  editingModel = null,
  existingCategories = []
}) => {

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    basePrice: '',
    estimatedDays: 7,
    category: ''
  });

  // ✅ NOUVEAU: Galerie d'images existantes (mode édition)
  const [galleryImages, setGalleryImages] = useState([]);

  // ✅ NOUVEAU: Fichiers en attente d'upload (mode création OU ajout en édition)
  const [pendingFiles, setPendingFiles] = useState([]);
  const [pendingPreviews, setPendingPreviews] = useState([]);

  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageActionLoading, setImageActionLoading] = useState(null); // id de l'image en cours d'action
  const [error, setError] = useState(null);
  const [validation, setValidation] = useState({});

  const isEditing = !!editingModel;

  // Remplir le formulaire à l'ouverture
  useEffect(() => {
    if (show) {
      if (editingModel) {
        setFormData({
          name: editingModel.name || '',
          description: editingModel.description || '',
          basePrice: editingModel.basePrice || '',
          estimatedDays: editingModel.estimatedDays || 7,
          category: editingModel.category || ''
        });
        // Charger la galerie existante
        setGalleryImages(Array.isArray(editingModel.images) ? editingModel.images : []);
      } else {
        setFormData({
          name: '',
          description: '',
          basePrice: '',
          estimatedDays: 7,
          category: ''
        });
        setGalleryImages([]);
      }
      // Reset des fichiers en attente
      setPendingFiles([]);
      setPendingPreviews([]);
      setError(null);
      setValidation({});
    }
  }, [show, editingModel]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setValidation(prev => ({ ...prev, [field]: null }));
  };

  // ============================================================
  // ===== GESTION DES FICHIERS EN ATTENTE (avant upload) =====
  // ============================================================

  /**
   * Sélection de fichiers depuis l'input.
   * Gère plusieurs fichiers d'un coup grâce à `multiple`.
   */
  const handleFilesSelected = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Validation de chaque fichier
    const validFiles = [];
    for (const file of files) {
      const v = modelService.validateImageFile(file);
      if (!v.isValid) {
        setError(`${file.name} : ${v.message}`);
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length === 0) return;
    setError(null);

    // En mode édition : upload immédiat (le modèle existe déjà)
    if (isEditing && editingModel?.id) {
      uploadFilesToGallery(validFiles, editingModel.id);
    } else {
      // En mode création : on stocke les fichiers, ils seront uploadés après création
      addToPendingFiles(validFiles);
    }

    // Reset de l'input pour permettre de re-sélectionner le même fichier
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  /**
   * Ajoute des fichiers à la liste en attente (mode création).
   */
  const addToPendingFiles = (files) => {
    setPendingFiles(prev => [...prev, ...files]);

    // Générer les previews
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setPendingPreviews(prev => [
          ...prev,
          { name: file.name, dataUrl: ev.target.result }
        ]);
      };
      reader.readAsDataURL(file);
    });
  };

  /**
   * Retire un fichier de la liste en attente (mode création uniquement).
   */
  const removePendingFile = (index) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
    setPendingPreviews(prev => prev.filter((_, i) => i !== index));
  };

  // ============================================================
  // ===== ACTIONS SUR LA GALERIE EXISTANTE (mode édition) =====
  // ============================================================

  /**
   * Upload immédiat de fichiers à la galerie d'un modèle existant.
   */
  const uploadFilesToGallery = async (files, modelId) => {
    setUploadingImage(true);
    setError(null);

    try {
      const newImages = [];
      for (const file of files) {
        const res = await modelService.uploadModelImage(modelId, file);
        if (res.success && res.data) {
          newImages.push(res.data);
        } else {
          console.warn('⚠️ Échec upload:', file.name, res.message);
        }
      }

      if (newImages.length > 0) {
        // Recharger la galerie depuis le serveur pour avoir l'état exact
        const refreshed = await modelService.getModelImages(modelId);
        if (refreshed.success) {
          setGalleryImages(refreshed.data);
        } else {
          // Fallback : on ajoute simplement les nouvelles à la liste locale
          setGalleryImages(prev => [...prev, ...newImages]);
        }
      }
    } catch (err) {
      console.error('❌ Erreur upload galerie:', err);
      setError('Erreur lors de l\'upload des images');
    } finally {
      setUploadingImage(false);
    }
  };

  /**
   * Définir une image comme principale.
   */
  const handleSetPrimary = async (imageId) => {
    if (!editingModel?.id) return;
    setImageActionLoading(imageId);
    setError(null);

    try {
      const res = await modelService.setPrimaryImage(editingModel.id, imageId);
      if (res.success) {
        // Mettre à jour localement (toutes les autres deviennent non-principales)
        setGalleryImages(prev =>
          prev.map(img => ({
            ...img,
            isPrimary: img.id === imageId
          }))
        );
      } else {
        setError(res.message || 'Erreur lors de la définition de l\'image principale');
      }
    } catch (err) {
      console.error('❌ Erreur set primary:', err);
      setError('Erreur lors de la définition de l\'image principale');
    } finally {
      setImageActionLoading(null);
    }
  };

  /**
   * Supprimer une image de la galerie.
   */
  const handleDeleteImage = async (imageId) => {
    if (!editingModel?.id) return;
    if (!window.confirm('Supprimer cette image ?')) return;

    setImageActionLoading(imageId);
    setError(null);

    try {
      const res = await modelService.deleteModelImage(editingModel.id, imageId);
      if (res.success) {
        // Recharger la galerie pour avoir le nouveau primary (promu côté backend)
        const refreshed = await modelService.getModelImages(editingModel.id);
        if (refreshed.success) {
          setGalleryImages(refreshed.data);
        } else {
          setGalleryImages(prev => prev.filter(img => img.id !== imageId));
        }
      } else {
        setError(res.message || 'Erreur lors de la suppression');
      }
    } catch (err) {
      console.error('❌ Erreur delete image:', err);
      setError('Erreur lors de la suppression de l\'image');
    } finally {
      setImageActionLoading(null);
    }
  };

  // ============================================================
  // ===== VALIDATION ET SOUMISSION =====
  // ============================================================

  const validate = () => {
    const errors = {};

    if (!formData.name || !formData.name.trim()) {
      errors.name = 'Le nom est obligatoire';
    }

    if (formData.basePrice && parseFloat(formData.basePrice) < 0) {
      errors.basePrice = 'Le prix doit être positif';
    }

    if (formData.estimatedDays && parseInt(formData.estimatedDays) < 1) {
      errors.estimatedDays = 'La durée doit être positive';
    }

    setValidation(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!validate()) return;

    setLoading(true);

    try {
      // 1. Créer ou mettre à jour le modèle
      let response;
      if (isEditing) {
        response = await modelService.updateModel(editingModel.id, formData);
      } else {
        response = await modelService.createModel(formData);
      }

      if (!response.success) {
        setError(response.message || 'Erreur lors de la sauvegarde');
        setLoading(false);
        return;
      }

      const savedModel = response.data;
      const modelId = isEditing ? editingModel.id : savedModel?.id;

      console.log(`✅ Modèle ${isEditing ? 'modifié' : 'créé'}:`, savedModel);

      // 2. ✅ NOUVEAU: Upload des fichiers en attente (mode création)
      if (!isEditing && pendingFiles.length > 0 && modelId) {
        setUploadingImage(true);
        for (const file of pendingFiles) {
          console.log('📸 Upload image:', file.name);
          const uploadRes = await modelService.uploadModelImage(modelId, file);
          if (!uploadRes.success) {
            console.warn('⚠️ Échec upload:', file.name, uploadRes.message);
            // On continue, le modèle est déjà créé
          }
        }
        setUploadingImage(false);
      }

      // 3. Succès final
      onSuccess?.(savedModel);
      onHide();

    } catch (err) {
      console.error('❌ Erreur:', err);
      setError(err.message || 'Erreur inattendue');
    } finally {
      setLoading(false);
      setUploadingImage(false);
    }
  };

  // ============================================================
  // ===== RENDER =====
  // ============================================================

  // Galerie à afficher : combinaison des images existantes + previews en attente
  const totalImagesCount = galleryImages.length + pendingPreviews.length;

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      backdrop="static"
    >
      <Modal.Header closeButton className="bg-light">
        <Modal.Title className="d-flex align-items-center gap-2">
          <Shirt size={20} className="text-primary" />
          {isEditing ? 'Modifier le modèle' : 'Nouveau modèle'}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Row>
            {/* Nom */}
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>
                  <Shirt size={14} className="me-1" />
                  Nom du modèle <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Ex: Boubou classique, Tailleur 3 pièces..."
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  isInvalid={!!validation.name}
                  autoFocus
                />
                <Form.Control.Feedback type="invalid">
                  {validation.name}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/* Description */}
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>
                  <FileText size={14} className="me-1" />
                  Description
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="Description optionnelle du modèle..."
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                />
              </Form.Group>
            </Col>

            {/* Prix de base */}
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  <DollarSign size={14} className="me-1" />
                  Prix de base (FCFA)
                </Form.Label>
                <InputGroup>
                  <Form.Control
                    type="number"
                    onWheel={e => e.target.blur()} // Empêche le scroll de changer la valeur
                    min="0"
                    step="500"
                    placeholder="25000"
                    value={formData.basePrice}
                    onChange={(e) => handleChange('basePrice', e.target.value)}
                    isInvalid={!!validation.basePrice}
                  />
                  <InputGroup.Text>FCFA</InputGroup.Text>
                  <Form.Control.Feedback type="invalid">
                    {validation.basePrice}
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>
            </Col>

            {/* Durée estimée */}
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  <Clock size={14} className="me-1" />
                  Durée de confection (jours)
                </Form.Label>
                <Form.Control
                  type="number"
                  onWheel={e => e.target.blur()} // Empêche le scroll de changer la valeur
                  min="1"
                  placeholder="7"
                  value={formData.estimatedDays}
                  onChange={(e) => handleChange('estimatedDays', e.target.value)}
                  isInvalid={!!validation.estimatedDays}
                />
                <Form.Control.Feedback type="invalid">
                  {validation.estimatedDays}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/* Catégorie */}
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>
                  <Tag size={14} className="me-1" />
                  Catégorie
                </Form.Label>
                <Form.Control
                  type="text"
                  list="categories-datalist"
                  placeholder="Ex: Boubou, Tailleur, Robe soirée..."
                  value={formData.category}
                  onChange={(e) => handleChange('category', e.target.value)}
                />
                {existingCategories.length > 0 && (
                  <datalist id="categories-datalist">
                    {existingCategories.map((cat, idx) => (
                      <option key={idx} value={cat} />
                    ))}
                  </datalist>
                )}
                <Form.Text className="text-muted">
                  Tapez une nouvelle catégorie ou choisissez parmi les existantes
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          {/* ============================================ */}
          {/* ===== ✅ GALERIE D'IMAGES ================ */}
          {/* ============================================ */}
          <hr className="my-4" />

          <div className="gallery-section">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h6 className="mb-0 d-flex align-items-center gap-2">
                <ImageIcon size={16} className="text-primary" />
                Photos du modèle
                {totalImagesCount > 0 && (
                  <span className="badge bg-light text-dark">
                    {totalImagesCount}
                  </span>
                )}
              </h6>

              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                type="button"
                disabled={uploadingImage || loading}
              >
                {uploadingImage ? (
                  <>
                    <Spinner size="sm" className="me-1" /> Upload...
                  </>
                ) : (
                  <>
                    <Plus size={14} className="me-1" /> Ajouter
                  </>
                )}
              </Button>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                multiple
                onChange={handleFilesSelected}
                style={{ display: 'none' }}
              />
            </div>

            {totalImagesCount === 0 ? (
              <div className="gallery-empty text-center p-4 border rounded bg-light">
                <ImageIcon size={40} className="text-muted mb-2" />
                <p className="text-muted mb-1 small">Aucune photo</p>
                <small className="text-muted">
                  JPG, PNG, WebP · Max 5MB par photo
                </small>
              </div>
            ) : (
              <Row className="g-2">
                {/* Images existantes (mode édition) */}
                {galleryImages.map((img) => {
                  const absUrl = modelService.buildImageUrl(img.imageUrl);
                  const isLoading = imageActionLoading === img.id;
                  return (
                    <Col xs={6} md={4} key={img.id}>
                      <div className={`gallery-item ${img.isPrimary ? 'is-primary' : ''}`}>
                        <img src={absUrl} alt="" />

                        {img.isPrimary && (
                          <div className="primary-badge">
                            <Star size={12} fill="currentColor" />
                            <span className="ms-1">Principale</span>
                          </div>
                        )}

                        <div className="gallery-actions">
                          {!img.isPrimary && (
                            <Button
                              variant="light"
                              size="sm"
                              className="action-btn"
                              onClick={() => handleSetPrimary(img.id)}
                              disabled={isLoading || uploadingImage}
                              title="Définir comme principale"
                              type="button"
                            >
                              {isLoading ? <Spinner size="sm" /> : <Star size={14} />}
                            </Button>
                          )}
                          <Button
                            variant="light"
                            size="sm"
                            className="action-btn text-danger"
                            onClick={() => handleDeleteImage(img.id)}
                            disabled={isLoading || uploadingImage}
                            title="Supprimer"
                            type="button"
                          >
                            {isLoading ? <Spinner size="sm" /> : <Trash2 size={14} />}
                          </Button>
                        </div>
                      </div>
                    </Col>
                  );
                })}

                {/* Previews en attente (mode création) */}
                {pendingPreviews.map((preview, idx) => (
                  <Col xs={6} md={4} key={`pending-${idx}`}>
                    <div className="gallery-item is-pending">
                      <img src={preview.dataUrl} alt={preview.name} />
                      <div className="pending-badge">En attente</div>
                      <div className="gallery-actions">
                        <Button
                          variant="light"
                          size="sm"
                          className="action-btn text-danger"
                          onClick={() => removePendingFile(idx)}
                          title="Retirer"
                          type="button"
                        >
                          <X size={14} />
                        </Button>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            )}

            <Form.Text className="text-muted d-block mt-2">
              {isEditing
                ? '⭐ Cliquez sur l\'étoile pour définir l\'image principale · 🗑️ pour supprimer'
                : 'La première photo deviendra automatiquement l\'image principale'}
            </Form.Text>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={onHide}
            disabled={loading || uploadingImage}
          >
            <X size={16} className="me-1" />
            Annuler
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={loading || uploadingImage}
          >
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                {uploadingImage ? 'Upload des images...' : 'Sauvegarde...'}
              </>
            ) : (
              <>
                <Save size={16} className="me-1" />
                {isEditing ? 'Mettre à jour' : 'Créer le modèle'}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>

      <style>{`
        .gallery-item {
          position: relative;
          width: 100%;
          aspect-ratio: 1;
          border-radius: 8px;
          overflow: hidden;
          background: #f8f9fa;
          border: 2px solid transparent;
          transition: all 0.2s ease;
        }

        .gallery-item img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        .gallery-item.is-primary {
          border-color: #ffc107;
          box-shadow: 0 0 0 2px rgba(255, 193, 7, 0.2);
        }

        .gallery-item.is-pending {
          border-color: #0dcaf0;
          opacity: 0.85;
        }

        .gallery-item:hover .gallery-actions {
          opacity: 1;
        }

        .gallery-actions {
          position: absolute;
          top: 6px;
          right: 6px;
          display: flex;
          gap: 4px;
          opacity: 0;
          transition: opacity 0.15s ease;
        }

        .action-btn {
          width: 28px;
          height: 28px;
          padding: 0 !important;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50% !important;
          box-shadow: 0 1px 4px rgba(0,0,0,0.2);
        }

        .primary-badge {
          position: absolute;
          bottom: 6px;
          left: 6px;
          background: #ffc107;
          color: #212529;
          border-radius: 12px;
          padding: 2px 8px;
          font-size: 0.7rem;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          box-shadow: 0 1px 3px rgba(0,0,0,0.2);
        }

        .pending-badge {
          position: absolute;
          bottom: 6px;
          left: 6px;
          background: #0dcaf0;
          color: white;
          border-radius: 12px;
          padding: 2px 8px;
          font-size: 0.7rem;
          font-weight: 600;
        }

        /* Sur mobile, toujours montrer les actions */
        @media (max-width: 768px) {
          .gallery-actions {
            opacity: 1;
          }
        }
      `}</style>
    </Modal>
  );
};

export default CreateModelModal;